import { Injectable } from "@angular/core";
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from "@angular/forms";

import { DffReactiveFormControlValueMapper as Map, updateDirty } from "@dffedb/forms";
import { EjendomsfaktorDefinition, EjendomsfaktorFormBuilder } from "@e-forsyning/common/ejendomsfaktor";
import { Huskeliste } from "@e-forsyning/common/huskeliste";
import { Maaler, MaalerType } from "@e-forsyning/common/maaler";
import { NoteBruger, NoteHenvendelsestype, NoteOmraade, NoteOpgavestyringStatus } from "@e-forsyning/common/note";

import { BatteriskiftFormBuilder } from "../opgave/arbejdsgang/batteriskift/batteriskift.form-builder";
import { BatteriskiftData } from "../opgave/arbejdsgang/batteriskift/model/batteriskift-data.model";
import { EjendomsfaktorerData } from "../opgave/arbejdsgang/ejendomsfaktorer/model/ejendomsfaktorer-data.model";
import { InstallationsoprettelseFormBuilder } from "../opgave/arbejdsgang/installationsoprettelse/installationsoprettelse.form-builder";
import { InstallationsoprettelseData } from "../opgave/arbejdsgang/installationsoprettelse/model/installationsoprettelse-data.model";
import { KomponentFormBuilder } from "../opgave/arbejdsgang/komponenter/komponent.form-builder";
import { KomponentGruppe } from "../opgave/arbejdsgang/komponenter/model/komponent-gruppe.model";
import { KomponentType } from "../opgave/arbejdsgang/komponenter/model/komponent-type.model";
import { MaalerskiftFormBuilder } from "../opgave/arbejdsgang/maalerskift/maalerskift.form-builder";
import { MaalerskiftData } from "../opgave/arbejdsgang/maalerskift/model/maalerskift-data.model";
import { ArbejdsgangData } from "../opgave/arbejdsgang/model/arbejdsgang-data.model";
import { Arbejdsgang } from "../opgave/arbejdsgang/model/arbejdsgang.model";
import { OpgaveNoterData } from "../opgave/arbejdsgang/noter/model/opgave-noter-data.model";
import { OpgaveNoteFormBuilder } from "../opgave/arbejdsgang/noter/opgave-note.form-builder";
import { SendMailTilForbrugerData } from "../opgave/arbejdsgang/send-mail-til-forbruger/model/send-mail-til-forbruger-data.model";
import { SendMailTilForbrugerFormBuilder } from "../opgave/arbejdsgang/send-mail-til-forbruger/send-mail-til-forbruger.form-builder";
import { OpgaveInstallation } from "../opgave/model/opgave-installation.model";
import { Opgave, opgaveVersion } from "../opgave/model/opgave.model";

@Injectable({ providedIn: "root" })
export class OpgaveFormBuilder {
    constructor(
        private readonly ejendomsfaktorFormBuilder: EjendomsfaktorFormBuilder,
        private readonly installationsoprettelseFormBuilder: InstallationsoprettelseFormBuilder,
        private readonly komponentFormBuilder: KomponentFormBuilder,
        private readonly maalerskiftFormBuilder: MaalerskiftFormBuilder,
        private readonly batteriskiftFormBuilder: BatteriskiftFormBuilder,
        private readonly installationNoteFormBuilder: OpgaveNoteFormBuilder,
        private readonly sendMailTilForbrugerFormBuilder: SendMailTilForbrugerFormBuilder
    ) {}

    public opgaveToFormGroup(
        opgave: Opgave,
        komponentTyper: KomponentType[],
        ejendomsfaktorDefinitioner: EjendomsfaktorDefinition[],
        maalerePaaLager: Maaler[],
        maalerTyper: MaalerType[],
        huskelister: Huskeliste[],
        noteOmraader: NoteOmraade[],
        noteHenvendelsestyper: NoteHenvendelsestype[],
        noteOpgavestyringStatusser: NoteOpgavestyringStatus[],
        noteBrugere: NoteBruger[]
    ): UntypedFormGroup {
        const result = new UntypedFormGroup({
            arbejdsgange: this.arbejdsgangeToFormArray(
                opgave.arbejdsgange,
                komponentTyper,
                ejendomsfaktorDefinitioner,
                maalerePaaLager,
                maalerTyper,
                huskelister,
                noteOmraader,
                noteHenvendelsestyper,
                noteOpgavestyringStatusser,
                noteBrugere
            ),
            id: new UntypedFormControl(opgave.id),
            installation: this.installationToFormGroup(opgave.installation),
            navn: new UntypedFormControl(opgave.navn),
            paabegyndt: new UntypedFormControl(opgave.paabegyndt || new Date()),
            uid: new UntypedFormControl(opgave.uid),
            version: new UntypedFormControl(opgave.version || opgaveVersion)
        });

        // Da vi sætter 'dirty' på elementer inden de er tilknyttet en parent, bliver vi nødt til at boble 'dirty' op i hierarkiet manuelt.
        updateDirty(result);

        return result;
    }

    public installationToFormGroup(installation: OpgaveInstallation = { ejendomNr: 0, installationNr: 0 }): UntypedFormGroup {
        const result = new UntypedFormGroup({
            ejendomNr: new UntypedFormControl(installation.ejendomNr),
            installationNr: new UntypedFormControl(installation.installationNr)
        });
        return result;
    }

    public arbejdsgangeToFormArray(
        arbejdsgange: Arbejdsgang[],
        komponentTyper: KomponentType[],
        ejendomsfaktorDefinitioner: EjendomsfaktorDefinition[],
        maalerePaaLager: Maaler[],
        maalerTyper: MaalerType[],
        huskelister: Huskeliste[],
        noteOmraader: NoteOmraade[],
        noteHenvendelsestyper: NoteHenvendelsestype[],
        noteOpgavestyringStatusser: NoteOpgavestyringStatus[],
        noteBrugere: NoteBruger[]
    ): UntypedFormArray {
        const result = arbejdsgange.map((arbejdsgang) =>
            this.arbejdsgangToFormGroup(
                arbejdsgang,
                komponentTyper,
                ejendomsfaktorDefinitioner,
                maalerePaaLager,
                maalerTyper,
                huskelister,
                noteOmraader,
                noteHenvendelsestyper,
                noteOpgavestyringStatusser,
                noteBrugere
            )
        );
        return new UntypedFormArray(result);
    }

    public arbejdsgangToFormGroup(
        arbejdsgang: Arbejdsgang,
        komponentTyper: KomponentType[],
        ejendomsfaktorDefinitioner: EjendomsfaktorDefinition[],
        maalerePaaLager: Maaler[],
        maalerTyper: MaalerType[],
        huskelister: Huskeliste[],
        noteOmraader: NoteOmraade[],
        noteHenvendelsestyper: NoteHenvendelsestype[],
        noteOpgavestyringStatusser: NoteOpgavestyringStatus[],
        noteBrugere: NoteBruger[]
    ): UntypedFormGroup {
        return new UntypedFormGroup({
            data: this.arbejdsgangDataToFormGroup(
                arbejdsgang.type,
                arbejdsgang.data,
                komponentTyper,
                ejendomsfaktorDefinitioner,
                maalerePaaLager,
                maalerTyper,
                huskelister,
                noteOmraader,
                noteHenvendelsestyper,
                noteOpgavestyringStatusser,
                noteBrugere
            ),
            type: new UntypedFormControl(arbejdsgang.type)
        });
    }

    public arbejdsgangDataToFormGroup(
        type: string,
        arbejdsgangData: ArbejdsgangData,
        komponentTyper: KomponentType[],
        ejendomsfaktorDefinitioner: EjendomsfaktorDefinition[],
        maalerePaaLager: Maaler[],
        maalerTyper: MaalerType[],
        huskelister: Huskeliste[],
        noteOmraader: NoteOmraade[],
        noteHenvendelsestyper: NoteHenvendelsestype[],
        noteOpgavestryingStatusser: NoteOpgavestyringStatus[],
        noteBrugere: NoteBruger[]
    ): UntypedFormGroup {
        switch (type) {
            case "ejendomsfaktorer":
                return this.ejendomsfaktorFormBuilder.ejendomsfaktorerDataToFormGroup(
                    arbejdsgangData as EjendomsfaktorerData,
                    ejendomsfaktorDefinitioner
                );
            case "komponentgruppe":
                return this.komponentFormBuilder.komponentGruppeToFormGroup(
                    arbejdsgangData as KomponentGruppe,
                    komponentTyper,
                    noteOmraader,
                    noteHenvendelsestyper,
                    noteOpgavestryingStatusser,
                    noteBrugere
                );
            case "noter":
                return this.installationNoteFormBuilder.installationNoterDataToFormGroup(
                    arbejdsgangData as OpgaveNoterData,
                    noteOmraader,
                    noteHenvendelsestyper,
                    noteOpgavestryingStatusser,
                    noteBrugere
                );
            case "maalerskift":
                return this.maalerskiftFormBuilder.maalerskiftDataToFormGroup(
                    arbejdsgangData as MaalerskiftData,
                    maalerePaaLager,
                    maalerTyper,
                    huskelister,
                    noteOmraader,
                    noteHenvendelsestyper,
                    noteOpgavestryingStatusser,
                    noteBrugere
                );
            case "installationsoprettelse":
                return this.installationsoprettelseFormBuilder.installationsoprettelseDataToFormGroup(
                    arbejdsgangData as InstallationsoprettelseData,
                    maalerePaaLager,
                    maalerTyper,
                    huskelister,
                    noteOmraader,
                    noteHenvendelsestyper,
                    noteOpgavestryingStatusser,
                    noteBrugere
                );
            case "send-mail-til-forbruger":
                return this.sendMailTilForbrugerFormBuilder.sendMailTilForbrugerDataToFormGroup(
                    arbejdsgangData as SendMailTilForbrugerData
                );
            case "batteriskift":
                return this.batteriskiftFormBuilder.batteriskiftDataToFormGroup(
                    arbejdsgangData as BatteriskiftData,
                    maalerTyper,
                    huskelister,
                    noteOmraader,
                    noteHenvendelsestyper,
                    noteOpgavestryingStatusser,
                    noteBrugere
                );
            default:
                return new UntypedFormGroup({ type: new UntypedFormControl(type) });
        }
    }

    public formGroupToOpgave(formGroup: UntypedFormGroup): Opgave {
        const result: Opgave = {
            arbejdsgange: this.formArrayToArbejdsgange(formGroup.controls["arbejdsgange"] as UntypedFormArray),
            id: Map.toInteger(formGroup.controls["id"]),
            installation: this.formGroupToInstallation(formGroup.controls["installation"] as UntypedFormGroup),
            navn: Map.toString(formGroup.controls["navn"]),
            paabegyndt: Map.toDate(formGroup.controls["paabegyndt"]),
            uid: Map.toString(formGroup.controls["uid"]),
            version: Map.toInteger(formGroup.controls["version"])
        };
        return result;
    }

    public formGroupToInstallation(formGroup: UntypedFormGroup): OpgaveInstallation {
        const result: OpgaveInstallation = {
            ejendomNr: Map.toInteger(formGroup.get("ejendomNr")),
            installationNr: Map.toInteger(formGroup.get("installationNr"))
        };
        return result;
    }

    public formArrayToArbejdsgange(formArray: UntypedFormArray): Arbejdsgang[] {
        const result = formArray.controls
            .filter((c) => c instanceof UntypedFormGroup)
            .map((c) => c as UntypedFormGroup)
            .map((formGroup) => this.formGroupToArbejdsgang(formGroup));
        return result;
    }

    public formGroupToArbejdsgang(formGroup: UntypedFormGroup): Arbejdsgang {
        const type = Map.toString(formGroup.controls["type"]);
        const result: Arbejdsgang = {
            data: this.formGroupToArbejdsgangData(type, formGroup.controls["data"] as UntypedFormGroup),
            type
        };
        return result;
    }

    public formGroupToArbejdsgangData(type: string, formGroup: UntypedFormGroup): ArbejdsgangData {
        switch (type) {
            case "ejendomsfaktorer":
                return this.ejendomsfaktorFormBuilder.formGroupToEjendomsfaktorerData(formGroup);
            case "komponentgruppe":
                return this.komponentFormBuilder.formGroupToKomponentGruppe(formGroup);
            case "noter":
                return this.installationNoteFormBuilder.formGroupToInstallationNoterData(formGroup);
            case "maalerskift":
                return this.maalerskiftFormBuilder.formGroupToMaalerskiftData(formGroup);
            case "installationsoprettelse":
                return this.installationsoprettelseFormBuilder.formGroupToInstallationsoprettelseData(formGroup);
            case "send-mail-til-forbruger":
                return this.sendMailTilForbrugerFormBuilder.formGroupTilSendMailTilForbrugerData(formGroup);
            case "batteriskift":
                return this.batteriskiftFormBuilder.formGroupToBatteriskiftData(formGroup);
            default:
                throw new Error(`Ukendt arbejdsgangstype: '${type}'`);
        }
    }
}
